import { FC, RefObject, useMemo } from 'react';

import { startOfDay, subDays } from 'date-fns';

import { FormLabel, HSpacingContainer, VSpacingContainer } from '@hh.ru/magritte-ui';
import { DropPlacement } from '@hh.ru/magritte-ui-drop/types';

import { DateSelect } from 'components/common/DateSelect/DateSelect';

interface DateSelectProps {
    placeholder: string;
    selectedDate: Date | undefined;
    onDateSelect: (date: string) => void;
    maxDate?: Date;
    minDate?: Date;
    inputDisabled?: boolean;
    inputInvalid?: boolean;
    errorMessage?: string;
    placement: DropPlacement | DropPlacement[];
}

interface PeriodProps {
    title?: string;
    dateStart: DateSelectProps;
    dateEnd: DateSelectProps;
    host?: RefObject<HTMLElement>;
    canBeSetPast?: boolean; // может ли поставить дату в прошлом, по умолчанию false
}

const TODAY = startOfDay(new Date());

export const Period: FC<PeriodProps> = ({ title, dateStart, dateEnd, host, canBeSetPast = false }) => {
    const startSelectedDate = dateStart.selectedDate;
    const endSelectedDate = dateEnd.selectedDate;

    const minStartDate = useMemo(() => {
        if (dateStart.minDate) {
            return dateStart.minDate;
        }
        return canBeSetPast ? undefined : TODAY;
    }, [canBeSetPast, dateStart.minDate]);

    const maxStartDate = useMemo(() => {
        if (dateStart.maxDate) {
            return dateStart.maxDate;
        }

        return endSelectedDate;
    }, [endSelectedDate, dateStart.maxDate]);

    const minEndDate = useMemo(() => {
        if (dateEnd.minDate) {
            return dateEnd.minDate;
        }
        const dayBeforeStart = startSelectedDate ? subDays(startSelectedDate, 1) : undefined;
        return canBeSetPast ? dayBeforeStart : dayBeforeStart || TODAY;
    }, [dateEnd.minDate, startSelectedDate, canBeSetPast]);

    return (
        <VSpacingContainer default={12}>
            {title && <FormLabel>{title}</FormLabel>}
            <HSpacingContainer default={12}>
                <DateSelect {...dateStart} minDate={minStartDate} maxDate={maxStartDate} dropHost={host} />
                <DateSelect {...dateEnd} minDate={minEndDate} maxDate={dateEnd.maxDate} dropHost={host} />
            </HSpacingContainer>
        </VSpacingContainer>
    );
};
